import React, { useState, useEffect } from 'react';
import { AppNotificationSetting, AppState } from '../types';
import { Bell, User, Flame, Droplets, Save, Check, RefreshCw, AlertCircle, Mail, Lock, Server, Send, Sparkles, Key, Calendar, Download, ExternalLink, CheckCircle2, Globe, Clock, Target } from 'lucide-react';
import { loadAppState, saveAppState, toPersianDigits, notifyAppSettingsUpdate } from '../lib/storage';
import { createGoogleCalendarUrl, generateIcsContent, generateWeeklyRoutineIcsContent, generateCombinedIcsContent, generateFamilyRemindersIcsContent, downloadIcsFile, getPersianTitle } from '../lib/calendar';
import { getInMemoryReminders } from '../lib/reminderStorage';
import { sendWorkerAiRequest, parseNutritionReply } from '../lib/workerAi';
import { getAppLanguage, setAppLanguage, Language, translate, formatNumberByLang } from '../lib/i18n';
import { formatGregorianDate } from '../lib/dateUtils';
import { apiGet, apiPut, fetchAuthMe, AuthUser } from '../lib/api';
import VoiceInputButton from './VoiceInputButton';

function FormattedAiCalorieAnalysis({ targetCalories, rawReply }: { targetCalories: number; rawReply: string }) {
  const currentLang = getAppLanguage();
  const cleanText = rawReply
    .replace(/\\\(|\\\)/g, '')
    .replace(/\\\[|\\\]/g, '')
    .replace(/\\text\s*\{([^}]*)\}/g, '$1')
    .replace(/\\text/g, '')
    .replace(/\\approx/g, ' ≈ ')
    .replace(/\\times/g, ' × ')
    .replace(/\\cdot/g, ' × ')
    .replace(/\\/g, '')
    .replace(/target_calories:\s*\d+/gi, '');

  const lines = cleanText.split('\n').map(l => l.trim()).filter(Boolean);

  return (
    <div className="mt-4 space-y-3 animate-fadeIn">
      {/* Top Banner Card */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-4 rounded-2xl shadow-md flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-xs">
            <Flame className="w-6 h-6 text-amber-300" />
          </div>
          <div>
            <span className="text-[10px] text-emerald-100 font-bold uppercase tracking-wider block">
              {translate(currentLang, 'aiCalorieBannerTitle', 'کالری هدف روزانه محاسبه‌شده')}
            </span>
            <h4 className="text-xl font-extrabold font-display flex items-baseline gap-1.5">
              <span>{formatNumberByLang(targetCalories, currentLang)}</span>
              <span className="text-xs font-normal text-emerald-100">
                {currentLang === 'sv' ? 'kcal / dag' : currentLang === 'en' ? 'kcal / day' : 'کیلوکالری / روز'}
              </span>
            </h4>
          </div>
        </div>
        <div className="bg-white/10 px-3 py-1.5 rounded-xl text-[11px] font-bold text-emerald-50 border border-white/20">
          {translate(currentLang, 'aiCalorieBannerSaved', 'ثبت و اعمال شد ✅')}
        </div>
      </div>

      {/* Structured Analysis Body */}
      <div className="bg-slate-50 dark:bg-gray-900 border border-indigo-100 dark:border-indigo-950 rounded-2xl p-4 space-y-2.5">
        <h5 className="text-xs font-bold text-indigo-900 dark:text-indigo-300 flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-800 pb-2">
          <Sparkles className="w-4 h-4 text-indigo-500" />
          {translate(currentLang, 'aiCalorieAnalysisTitle', 'تحلیل علمی و آناتومیک هوش مصنوعی')}
        </h5>

        <div className="space-y-2 text-xs text-gray-800 dark:text-gray-200 leading-relaxed">
          {lines.map((line, idx) => {
            const isHeader = line.startsWith('**') && line.endsWith('**');
            const cleanLine = line.replace(/\*\*/g, '').trim();

            if (!cleanLine) return null;

            if (isHeader || line.startsWith('###') || line.startsWith('##')) {
              return (
                <div key={idx} className="font-bold text-indigo-600 dark:text-indigo-400 mt-2.5 text-xs flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full inline-block"></span>
                  {currentLang === 'fa' ? toPersianDigits(cleanLine) : cleanLine}
                </div>
              );
            }

            if (line.startsWith('-') || line.startsWith('*')) {
              const bulletText = cleanLine.replace(/^[-*]\s*/, '');
              return (
                <div key={idx} className="flex items-start gap-2 bg-white dark:bg-gray-800 p-2.5 rounded-xl border border-gray-100 dark:border-gray-700/60 shadow-xs">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span className="text-[11px] leading-relaxed">
                    {currentLang === 'fa' ? toPersianDigits(bulletText) : bulletText}
                  </span>
                </div>
              );
            }

            return (
              <p key={idx} className="text-[11px] text-gray-700 dark:text-gray-300 leading-relaxed">
                {currentLang === 'fa' ? toPersianDigits(cleanLine) : cleanLine}
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface SettingsProps {
  onSettingsUpdated: () => void;
  onNavigateToSchedules?: () => void;
}

export default function SettingsModule({ onSettingsUpdated, onNavigateToSchedules }: SettingsProps) {
  const [appState, setAppState] = useState<AppState>(loadAppState());
  const [saved, setSaved] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Authenticated user profile states
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [editingName, setEditingName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaveSuccess, setProfileSaveSuccess] = useState(false);

  // AI Calorie Calculator states
  const [calculatingAiCalories, setCalculatingAiCalories] = useState(false);
  const [aiCalorieData, setAiCalorieData] = useState<{ targetCalories: number; rawReply: string } | null>(null);

  // Fetch current authenticated user identity
  useEffect(() => {
    let isMounted = true;
    const loadUserIdentity = async () => {
      try {
        const authResult = await fetchAuthMe();
        if (isMounted && authResult.authenticated && authResult.user) {
          setCurrentUser(authResult.user);
          setEditingName(authResult.user.display_name || authResult.user.displayName || '');
        }
      } catch (err) {
        console.warn('Could not load user identity:', err);
      }
    };
    loadUserIdentity();
    return () => { isMounted = false; };
  }, []);

  // Fetch real settings from Cloudflare D1 on mount
  useEffect(() => {
    let isMounted = true;
    const fetchD1Settings = async () => {
      try {
        const res = await apiGet<any>('/api/settings');
        if (isMounted && res.success && res.data) {
          const fetched = res.data;
          const settingData = fetched.setting_data || fetched.settings || {};
          setAppState(prev => {
            const hVal = fetched.height_cm ?? fetched.height ?? settingData.height ?? settingData.heightCm ?? prev.settings.height;
            const wgVal = fetched.target_weight_kg ?? fetched.weightGoal ?? settingData.weightGoal ?? settingData.target_weight_kg ?? prev.settings.weightGoal;
            const bfgVal = fetched.target_body_fat_pct ?? fetched.bodyFatGoal ?? settingData.bodyFatGoal ?? settingData.target_body_fat_pct ?? prev.settings.bodyFatGoal;
            const cwVal = fetched.currentWeight ?? fetched.current_weight ?? settingData.currentWeight ?? prev.settings.currentWeight ?? null;

            return {
              ...prev,
              settings: {
                ...prev.settings,
                language: fetched.language || settingData.language || prev.settings.language,
                theme: fetched.theme || settingData.theme || prev.settings.theme,
                calendarType: fetched.calendar_type || settingData.calendarType || prev.settings.calendarType,
                targetWater: fetched.target_water ?? settingData.targetWater ?? prev.settings.targetWater,
                targetWaterMl: fetched.target_water ?? settingData.targetWaterMl ?? prev.settings.targetWater,
                targetCalories: fetched.target_calories ?? settingData.targetCalories ?? prev.settings.targetCalories,
                smtpHost: fetched.smtp_host || settingData.smtpHost || prev.settings.smtpHost,
                smtpPort: fetched.smtp_port ? String(fetched.smtp_port) : (settingData.smtpPort || prev.settings.smtpPort),
                smtpUsername: fetched.smtp_username || settingData.smtpUsername || prev.settings.smtpUsername,
                smtpFrom: fetched.smtp_from || settingData.smtpFrom || prev.settings.smtpFrom,
                notifications: Array.isArray(settingData.notifications) ? settingData.notifications : prev.settings.notifications,
                age: fetched.age ?? settingData.age ?? prev.settings.age,
                gender: fetched.gender ?? settingData.gender ?? prev.settings.gender,
                activityLevel: fetched.activity_level ?? fetched.activityLevel ?? settingData.activityLevel ?? prev.settings.activityLevel,
                weightGoalType: fetched.weight_goal_type ?? fetched.weightGoalType ?? settingData.weightGoalType ?? prev.settings.weightGoalType,
                medicalConditions: fetched.medical_history ?? fetched.medicalConditions ?? settingData.medicalConditions ?? prev.settings.medicalConditions,
                medications: fetched.medications ?? settingData.medications ?? prev.settings.medications,
                allergies: fetched.allergies ?? settingData.allergies ?? prev.settings.allergies,
                dietaryGoals: fetched.health_goals ?? fetched.dietaryGoals ?? settingData.dietaryGoals ?? prev.settings.dietaryGoals,
                currentWeight: cwVal,
                height: hVal,
                weightGoal: wgVal,
                bodyFatGoal: bfgVal
              }
            };
          });
        }
      } catch (err) {
        console.warn('Could not fetch settings from Cloudflare D1:', err);
      }
    };

    fetchD1Settings();
    return () => { isMounted = false; };
  }, []);

  const saveSettingsToD1 = async (updatedSettings: AppState['settings']) => {
    try {
      notifyAppSettingsUpdate(updatedSettings);
      await apiPut('/api/settings', {
        language: updatedSettings.language,
        theme: updatedSettings.theme,
        calendar_type: updatedSettings.calendarType,
        target_water: updatedSettings.targetWaterMl ?? updatedSettings.targetWater,
        target_calories: updatedSettings.targetCalories,
        height_cm: updatedSettings.height,
        target_weight_kg: updatedSettings.weightGoal,
        target_body_fat_pct: updatedSettings.bodyFatGoal,
        current_weight: updatedSettings.currentWeight,
        age: updatedSettings.age,
        gender: updatedSettings.gender,
        activity_level: updatedSettings.activityLevel,
        weight_goal_type: updatedSettings.weightGoalType,
        medical_history: updatedSettings.medicalConditions,
        medications: updatedSettings.medications,
        allergies: updatedSettings.allergies,
        health_goals: updatedSettings.dietaryGoals,
        smtp_host: updatedSettings.smtpHost,
        smtp_port: updatedSettings.smtpPort ? Number(updatedSettings.smtpPort) : null,
        smtp_username: updatedSettings.smtpUsername,
        smtp_password: updatedSettings.smtpPassword,
        smtp_from: updatedSettings.smtpFrom,
        notifications_enabled: updatedSettings.notifications?.some(n => n.enabled) ?? true,
        setting_data: {
          language: updatedSettings.language,
          theme: updatedSettings.theme,
          calendarType: updatedSettings.calendarType,
          targetWater: updatedSettings.targetWater,
          targetWaterMl: updatedSettings.targetWaterMl ?? updatedSettings.targetWater,
          targetCalories: updatedSettings.targetCalories,
          smtpHost: updatedSettings.smtpHost,
          smtpPort: updatedSettings.smtpPort,
          smtpUsername: updatedSettings.smtpUsername,
          smtpFrom: updatedSettings.smtpFrom,
          notifications: updatedSettings.notifications,
          age: updatedSettings.age,
          gender: updatedSettings.gender,
          currentWeight: updatedSettings.currentWeight,
          height: updatedSettings.height,
          activityLevel: updatedSettings.activityLevel,
          weightGoal: updatedSettings.weightGoal,
          bodyFatGoal: updatedSettings.bodyFatGoal,
          weightGoalType: updatedSettings.weightGoalType,
          medicalConditions: updatedSettings.medicalConditions,
          medications: updatedSettings.medications,
          allergies: updatedSettings.allergies,
          dietaryGoals: updatedSettings.dietaryGoals
        }
      });
    } catch (err) {
      console.error('Failed to persist user settings to Cloudflare D1:', err);
    }
  };

  const currentLanguage: Language = appState.settings.language || getAppLanguage();

  const handleLanguageChange = (newLang: Language) => {
    setAppLanguage(newLang);
    const updatedSettings = {
      ...appState.settings,
      language: newLang
    };
    const updatedState: AppState = {
      ...appState,
      settings: updatedSettings
    };
    setAppState(updatedState);
    saveSettingsToD1(updatedSettings);
    onSettingsUpdated();
  };

  const handleToggleNotification = (id: string) => {
    const updatedNotifs = appState.settings.notifications.map(n => 
      n.id === id ? { ...n, enabled: !n.enabled } : n
    );
    const updatedSettings = {
      ...appState.settings,
      notifications: updatedNotifs
    };
    const updatedState = {
      ...appState,
      settings: updatedSettings
    };
    setAppState(updatedState);
    saveSettingsToD1(updatedSettings);
    onSettingsUpdated();
  };

  const handleTimeChange = (id: string, timeValue: string) => {
    const updatedNotifs = appState.settings.notifications.map(n => 
      n.id === id ? { ...n, time: timeValue } : n
    );
    const updatedSettings = {
      ...appState.settings,
      notifications: updatedNotifs
    };
    const updatedState = {
      ...appState,
      settings: updatedSettings
    };
    setAppState(updatedState);
    saveSettingsToD1(updatedSettings);
    onSettingsUpdated();
  };

  const handleProfileChange = (field: keyof AppState['settings'] | string, value: any) => {
    const updatedSettings = {
      ...appState.settings,
      [field]: value
    };
    const updatedState = {
      ...appState,
      settings: updatedSettings
    };
    setAppState(updatedState);
    saveSettingsToD1(updatedSettings);
    onSettingsUpdated();
  };

  const handleSaveSettings = () => {
    saveSettingsToD1(appState.settings);
    setSaved(true);
    onSettingsUpdated();
    setTimeout(() => setSaved(false), 2000);
    // Trigger simulated native mobile local push registering!
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('یادآورهای وکسا فعال شدند', { body: 'ساعت‌های یادآوری روزانه شما با موفقیت در سیستم ثبت گردید!' });
      }
    } catch(err) {}
  };

  const handleRequestPushPermission = () => {
    try {
      if ('Notification' in window) {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            alert('اعلان‌های محلی فعال گردید! اعلان آزمایشی ارسال شد.');
            new Notification('سامانه خودمراقبتی وکسا فعال شد', { body: 'سیستم ثبت و پایش هوشمند دیسک کمر شروع به کار کرد.' });
          }
        });
      } else {
        alert('اعلان‌های مرورگر توسط دستگاه شما پشتیبانی نمی‌شوند.');
      }
    } catch (e) {
      alert('درخواست فعال‌سازی اعلان‌های سیستمی ارسال شد.');
    }
  };

  const handleSmtpChange = (field: string, value: any) => {
    const updatedState = {
      ...appState,
      settings: {
        ...appState.settings,
        smtpSettings: {
          ...(appState.settings.smtpSettings || {
            host: '',
            port: 587,
            user: '',
            pass: '',
            secure: false,
            fromName: 'پیشرفت - یادآور هوشمند',
            fromEmail: '',
            toEmail: ''
          }),
          [field]: value
        }
      }
    };
    setAppState(updatedState);
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    setTestResult(null);
    try {
      const response = await fetch('/api/send-test-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          smtpSettings: appState.settings.smtpSettings,
          targetEmail: appState.settings.smtpSettings?.toEmail || appState.settings.smtpSettings?.user
        })
      });
      
      const responseText = await response.text();
      let data: any = {};
      try {
        data = JSON.parse(responseText);
      } catch (jsonErr) {
        // If it's HTML or empty, it could be a gateway timeout or server error
        console.error("Failed to parse SMTP response as JSON:", jsonErr);
        if (response.status === 504 || response.status === 502) {
          throw new Error('سیستم با خطای زمان‌انتظار (Gateway Timeout 504) مواجه شد. این موضوع معمولاً به دلیل مسدود بودن پورت‌های خروجی ایمیل (پورت ۵۸۷ یا ۴۶۵) توسط فایروال سرور ابری یا مسدود بودن IP سرور از طرف گوگل/ارائه‌دهنده SMTP است.');
        } else {
          throw new Error(`پاسخ غیرمنتظره از سرور (کد ${response.status}): ${responseText.substring(0, 150)}...`);
        }
      }

      if (response.ok && data.success) {
        setTestResult({ success: true, message: 'ایمیل آزمایشی با موفقیت ارسال شد! لطفاً صندوق ورودی خود را بررسی کنید.' });
      } else {
        let errMsg = data.error || 'ارسال ایمیل آزمایشی با خطا مواجه شد.';
        if (errMsg.includes('ETIMEDOUT') || errMsg.includes('connect ECONNREFUSED') || errMsg.includes('timeout')) {
          errMsg = `خطای اتصال به سرور SMTP: اتصال برقرار نشد (پورت خروجی یا IP مسدود است). این امر در سرورهای ابری بسیار معمول است. شما همچنان می‌توانید از شبیه‌ساز ایمیل فوق‌العاده برنامه استفاده کنید.`;
        } else if (errMsg.includes('Username and Password not accepted') || errMsg.includes('Invalid credentials')) {
          errMsg = `نام کاربری یا رمز عبور (App Password) پذیرفته نشد. لطفاً مطمئن شوید که تأیید دومرحله‌ای حساب گوگل شما فعال است و از رمز عبور ۱۶ رقمی برنامه (App Password) به‌جای رمز عبور اصلی ایمیل استفاده می‌کنید.`;
        }
        setTestResult({ success: false, message: errMsg });
      }
    } catch (err: any) {
      let friendlyError = err.message || 'خطا در برقراری ارتباط با سرور.';
      if (friendlyError.includes('Unexpected end of JSON input') || friendlyError.includes('Failed to fetch')) {
        friendlyError = 'خطای اتصال به سرور یادآور. این امر معمولاً به دلیل محدودیت‌های شبکه ابری رخ می‌دهد. شبیه‌ساز ایمیل برنامه فعال است و تمام یادآوری‌ها را ذخیره می‌کند.';
      }
      setTestResult({ success: false, message: friendlyError });
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleDownloadAllCalendarIcs = () => {
    const weeklyRoutine = appState.settings.weeklyRoutine || [];
    const reminders = getInMemoryReminders();
    const icsContent = generateCombinedIcsContent(weeklyRoutine, reminders);
    if (!icsContent) {
      alert('هیچ برنامه هفتگی یا یادآوری خانوادگی فعالی برای دانلود وجود ندارد.');
      return;
    }
    downloadIcsFile('progress-full-schedule-and-reminders.ics', icsContent);
  };

  return (
    <div id="settings-module-view" className="space-y-6 animate-fadeIn">
      
      {/* Header */}
      <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between border-b pb-4 border-gray-100 dark:border-gray-800">
        <div>
          <h2 className="text-2xl font-semibold font-display text-gray-900 dark:text-white flex items-center gap-2">
            <Bell className="w-6 h-6 text-indigo-500" />
            {translate(currentLanguage, 'settingsTitle', 'تنظیمات عمومی و ساعت یادآورها')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {translate(currentLanguage, 'settingsDesc', 'ساعت‌های هشدار روزانه، اهداف فیزیکی و کالری مورد نظر، نام کاربری و تنظیمات نوتیفیکیشن را شخصی‌سازی کنید.')}
          </p>
        </div>
        <button
          onClick={handleSaveSettings}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 self-start md:self-center"
        >
          {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saved ? translate(currentLanguage, 'settingsSaved', 'تغییرات ذخیره شد!') : translate(currentLanguage, 'settingsSaveBtn', 'ذخیره تنظیمات')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left: Settings Cards (8 cols) */}
        <div className="lg:col-span-8 space-y-4">
          
          {/* Language Selection Card */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4 border border-indigo-200 dark:border-indigo-900/60 shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl">
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white font-display">
                    {translate(currentLanguage, 'languageSectionTitle', 'زبان و منطقه جغرافیایی (Language & Region)')}
                  </h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {translate(currentLanguage, 'languageSectionDesc', 'زبان نمایش رابط کاربری و جهت چیدمان برنامه را انتخاب کنید.')}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => handleLanguageChange('fa')}
                className={`p-3.5 rounded-2xl border flex flex-col items-center justify-center gap-2 transition-all cursor-pointer ${
                  currentLanguage === 'fa'
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md ring-2 ring-indigo-300 dark:ring-indigo-800'
                    : 'bg-gray-50 dark:bg-gray-900/60 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800 hover:border-indigo-300'
                }`}
              >
                <span className="text-2xl">🇮🇷</span>
                <span className="text-xs font-bold font-display">فارسی</span>
                <span className="text-[10px] opacity-80 font-mono">Persian (RTL)</span>
              </button>

              <button
                type="button"
                onClick={() => handleLanguageChange('en')}
                className={`p-3.5 rounded-2xl border flex flex-col items-center justify-center gap-2 transition-all cursor-pointer ${
                  currentLanguage === 'en'
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md ring-2 ring-indigo-300 dark:ring-indigo-800'
                    : 'bg-gray-50 dark:bg-gray-900/60 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800 hover:border-indigo-300'
                }`}
              >
                <span className="text-2xl">🇬🇧</span>
                <span className="text-xs font-bold font-display">English</span>
                <span className="text-[10px] opacity-80 font-mono">English (LTR)</span>
              </button>

              <button
                type="button"
                onClick={() => handleLanguageChange('sv')}
                className={`p-3.5 rounded-2xl border flex flex-col items-center justify-center gap-2 transition-all cursor-pointer ${
                  currentLanguage === 'sv'
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md ring-2 ring-indigo-300 dark:ring-indigo-800'
                    : 'bg-gray-50 dark:bg-gray-900/60 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800 hover:border-indigo-300'
                }`}
              >
                <span className="text-2xl">🇸🇪</span>
                <span className="text-xs font-bold font-display">Svenska</span>
                <span className="text-[10px] opacity-80 font-mono">Swedish (LTR)</span>
              </button>
            </div>
          </div>

          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                {translate(currentLanguage, 'settingsAlarmsTitle', 'زمان‌بندی آلارم و هشدارهای روتین')}
              </h3>
              <button 
                onClick={handleRequestPushPermission}
                className="text-xs text-indigo-500 hover:text-indigo-600 font-bold flex items-center gap-1 cursor-pointer"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                {translate(currentLanguage, 'settingsNotificationTestBtn', 'تست و فعال‌سازی نوتیفیکیشن')}
              </button>
            </div>

            <div className="p-4 rounded-xl bg-indigo-50/70 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/40 space-y-3">
              <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
                <Bell className="w-5 h-5 text-indigo-500 shrink-0" />
                <h4 className="font-bold text-xs">
                  {translate(currentLanguage, 'settingsAlarmsIntegratedTitle', 'مدیریت یکپارچه آلارم‌ها و هشدارهای روزانه در برنامه هفتگی')}
                </h4>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                {translate(currentLanguage, 'settingsAlarmsIntegratedDesc', 'تمامی آلارم‌ها، هشدارهای گوشی، و یادآورهای روتین روزانه شما از این پس به صورت مستقیم و اختصاصی در بخش «برنامه‌ریزی و روتین‌های هفتگی» تنظیم می‌شوند. برای هر فعالیت می‌توانید هشدار نوتیفیکیشن گوشی و همگام‌سازی تقویم گوگل را به دلخواه فعال کنید.')}
              </p>
              {onNavigateToSchedules && (
                <button
                  type="button"
                  onClick={onNavigateToSchedules}
                  className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 cursor-pointer"
                >
                  <Calendar className="w-4 h-4" />
                  <span>{translate(currentLanguage, 'settingsGoToWeeklySchedule', 'ورود به تنظیمات برنامه هفتگی و آلارم‌ها')}</span>
                </button>
              )}
            </div>

            {/* Google Calendar & .ics Direct Integration Card */}
            <div className="p-4 rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-emerald-500/5 dark:bg-gray-900/60 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 rounded-lg">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-900 dark:text-white font-display">
                      {translate(currentLanguage, 'settingsCalendarExportTitle', 'خروجی تقویم جامع برنامه هفتگی و مرکز یادآورهای خانوادگی (.ics)')}
                    </h4>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleDownloadAllCalendarIcs}
                  className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 shrink-0 self-start sm:self-auto cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  <span>{translate(currentLanguage, 'settingsDownloadIcsBtn', 'دانلود تقویم همه‌کاره (.ics)')}</span>
                </button>
              </div>

              <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-relaxed border-t border-emerald-100 dark:border-emerald-900/30 pt-2">
                {translate(currentLanguage, 'settingsIcsGuide', '💡 راهنما: با کلیک روی دکمه فوق، فایل جامع .ics شامل تمام روتین‌های هفتگی و یادآورهای فعال مرکز یادآورهای خانوادگی دانلود می‌شود. با وارد کردن این فایل در Google Calendar، آیفون یا سامسونگ، تمام آلارم‌ها و یادآورهای شما روی تقویم بومی گوشی فعال می‌گردند.')}
              </p>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl text-xs text-amber-800 dark:text-amber-200 flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold block mb-0.5">{translate(currentLanguage, 'settingsNotificationNoticeTitle', 'نکته همگام‌سازی نوتیفیکیشن‌های دستگاه')}</span>
                {translate(currentLanguage, 'settingsNotificationNoticeDesc', 'اعلان‌های هوشمند برنامه به اجازه دسترسی در مرورگر شما (Notification Permission) متکی هستند. لطفاً دسترسی نوتیفیکیشن را در مرورگر تلفن همراه یا دسکتاپ خود صادر کنید تا یادآورها را منظم دریافت کنید.')}
              </div>
            </div>

            {/* Premium iOS / iPhone PWA Notification Activation Guide */}
            <div className="p-5 rounded-xl border border-indigo-100 dark:border-indigo-950/40 bg-indigo-500/5 dark:bg-gray-900/60 space-y-3.5">
              <h4 className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider flex items-center gap-1.5">
                <span>{translate(currentLanguage, 'settingsIosNoticeTitle', '📱 راهنمای مخصوص کاربران آیفون (iOS) برای فعال‌سازی اعلان‌ها')}</span>
              </h4>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                {translate(currentLanguage, 'settingsIosNoticeDesc', 'سیستم‌عامل iOS اپل به دلایل امنیتی، دریافت نوتیفیکیشن را در صفحات وب معمولی غیرفعال می‌کند. برای حل این مشکل و دریافت منظم آلارم‌ها، لطفاً مراحل کوتاه زیر را انجام دهید:')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
                <div className="p-3 bg-white dark:bg-gray-950 rounded-lg border border-gray-100 dark:border-gray-800/80 space-y-1">
                  <span className="font-bold text-indigo-600 dark:text-indigo-400 block">{translate(currentLanguage, 'settingsIosStep1Title', 'مرحله اول: افزودن به صفحه اصلی')}</span>
                  <p className="text-gray-500 dark:text-gray-400">{translate(currentLanguage, 'settingsIosStep1Desc', 'در مرورگر Safari گوشی خود، دکمه Share (اشتراک‌گذاری) را در پایین فشار داده و سپس گزینه Add to Home Screen را انتخاب کنید.')}</p>
                </div>
                <div className="p-3 bg-white dark:bg-gray-950 rounded-lg border border-gray-100 dark:border-gray-800/80 space-y-1">
                  <span className="font-bold text-indigo-600 dark:text-indigo-400 block">{translate(currentLanguage, 'settingsIosStep2Title', 'مرحله دوم: تایید دسترسی')}</span>
                  <p className="text-gray-500 dark:text-gray-400">{translate(currentLanguage, 'settingsIosStep2Desc', 'برنامه وکسا را از روی صفحه اصلی آیفون باز کنید، به این بخش (تنظیمات) آمده و روی دکمه تست و فعال‌سازی نوتیفیکیشن بزنید تا مجوز صادر شود.')}</p>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Right: Personal Profile & Target thresholds (4 cols) */}
        <div className="lg:col-span-4 space-y-4">
          
          {/* USER ACCOUNT & PROFILE CARD */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4 border border-indigo-200 dark:border-indigo-900/60 shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl">
                  <User className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-gray-900 dark:text-white font-display">
                    {translate(currentLanguage, 'settingsProfileHeader', 'پروفایل و حساب کاربری')}
                  </h3>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">
                    {translate(currentLanguage, 'settingsProfileSub', 'مشخصات احرازشده در دیتابیس Cloudflare D1')}
                  </p>
                </div>
              </div>
              {currentUser?.is_google_user ? (
                <span className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/80 text-blue-600 dark:text-blue-300 text-[10px] font-bold border border-blue-200 dark:border-blue-800 flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  گوگل OAuth
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/80 text-emerald-600 dark:text-emerald-300 text-[10px] font-bold border border-emerald-200 dark:border-emerald-800 flex items-center gap-1">
                  <Key className="w-3 h-3" />
                  حساب D1
                </span>
              )}
            </div>

            <div className="flex flex-col items-center sm:items-start text-center sm:text-right gap-3 p-3 bg-gray-50 dark:bg-gray-900/60 rounded-2xl border border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-3 w-full">
                {/* Avatar */}
                <div className="relative shrink-0">
                  {currentUser?.photo_url ? (
                    <img
                      src={currentUser.photo_url}
                      alt={currentUser.display_name || 'کاربر'}
                      referrerPolicy="no-referrer"
                      className="w-12 h-12 rounded-2xl object-cover ring-2 ring-indigo-500/30 shadow-xs"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center font-bold text-base shadow-xs">
                      {currentUser?.display_name ? currentUser.display_name.charAt(0).toUpperCase() : 'U'}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0 space-y-1">
                  <label className="text-[9px] font-bold text-gray-400 uppercase block">
                    {translate(currentLanguage, 'settingsEmailLabel', 'ایمیل حساب احرازشده')}
                  </label>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg text-xs font-mono text-gray-700 dark:text-gray-300 truncate">
                    <Mail className="w-3 h-3 text-gray-400 shrink-0" />
                    <span className="truncate">{currentUser?.email || 'در حال دریافت...'}</span>
                  </div>
                </div>
              </div>

              {/* Display Name Section */}
              <div className="w-full space-y-1.5 pt-2 border-t border-gray-200/60 dark:border-gray-800">
                <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase block">
                  {translate(currentLanguage, 'settingsNameLabel', 'نام یا نام مستعار شما')}
                </label>
                {currentUser?.is_google_user ? (
                  <div className="space-y-1">
                    <div className="px-3 py-1.5 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-xl text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center justify-between">
                      <span>{currentUser.display_name || currentUser.email}</span>
                      <span className="text-[10px] text-blue-500 font-normal">نام گوگل (مستقیم)</span>
                    </div>
                    <p className="text-[10px] text-gray-400 leading-tight">
                      * اطلاعات هویت حساب‌های گوگل مستقیماً از گوگل فراخوانی می‌شوند و در localStorage یا دیتابیس محلی ذخیره یا تغییر داده نمی‌شوند.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      placeholder={translate(currentLanguage, 'settingsNamePlaceholder', 'نام شما')}
                      className="flex-1 px-3 py-1.5 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white"
                    />
                    <button
                      type="button"
                      disabled={savingProfile || !editingName.trim()}
                      onClick={async () => {
                        setSavingProfile(true);
                        try {
                          const res = (await apiPut<any>('/api/auth/profile', { display_name: editingName })) as any;
                          if (res.success && res.user) {
                            setCurrentUser(res.user);
                            handleProfileChange('userName', res.user.display_name || '');
                            setProfileSaveSuccess(true);
                            setTimeout(() => setProfileSaveSuccess(false), 2500);
                          }
                        } catch (err) {
                          console.error('Failed to update name:', err);
                        } finally {
                          setSavingProfile(false);
                        }
                      }}
                      className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-all shadow-xs flex items-center gap-1 cursor-pointer disabled:opacity-50 shrink-0"
                    >
                      {savingProfile ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      <span>{profileSaveSuccess ? 'ثبت شد!' : 'ذخیره نام'}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* HEALTH TARGETS CARD */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider block">
              {translate(currentLanguage, 'settingsHealthGoalsHeader', 'اهداف روزانه سلامت و تغذیه')}
            </h3>

            {/* Daily Target calories */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase flex items-center gap-1">
                <Flame className="w-3.5 h-3.5 text-orange-400" />
                {translate(currentLanguage, 'settingsCalorieGoalLabel', 'کالری هدف تغذیه روزانه (کیلوکالری)')}
              </label>
              <input 
                type="number" 
                value={appState.settings.targetCalories}
                onChange={(e) => handleProfileChange('targetCalories', Number(e.target.value))}
                placeholder="2200" 
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl text-xs focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white font-mono"
              />
            </div>

            {/* Target water intake */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase flex items-center gap-1">
                <Droplets className="w-3.5 h-3.5 text-sky-400" />
                {translate(currentLanguage, 'settingsWaterGoalLabel', 'میزان آب هدف روزانه (میلی‌لیتر)')}
              </label>
              <input 
                type="number" 
                value={appState.settings.targetWater}
                onChange={(e) => handleProfileChange('targetWater', Number(e.target.value))}
                placeholder="2000" 
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl text-xs focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white font-mono"
              />
            </div>

            <p className="text-[10px] text-gray-400 leading-relaxed pt-2 border-t border-gray-100 dark:border-gray-800">
              {translate(currentLanguage, 'settingsLocalStorageNotice', 'کلیه داده‌ها، تنظیمات و اطلاعات سلامت شما به صورت مستقیم، امن و بی‌واسطه در پایگاه‌داده ابری Cloudflare D1 ذخیره و همگام‌سازی می‌شوند.')}
            </p>
          </div>

          {/* SMART BODY & CALORIE CALCULATOR CARD */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4 border-2 border-indigo-500/20 shadow-md">
            <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 pb-3">
              <div className="p-2 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-900 dark:text-white font-display">
                  {translate(currentLanguage, 'settingsCalcHeader', 'محاسبه‌گر قد، وزن و کالری هدف روزانه')}
                </h3>
                <span className="text-[10px] text-gray-400">
                  {translate(currentLanguage, 'settingsCalcSub', 'فرمول استاندارد Mifflin-St Jeor بر اساس آناتومی')}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1">{translate(currentLanguage, 'settingsHeightLabel', 'قد (سانتی‌متر)')}</label>
                <input
                  type="number"
                  value={appState.settings.height || ''}
                  onChange={(e) => handleProfileChange('height', Number(e.target.value))}
                  placeholder="175"
                  className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 font-mono text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1">{translate(currentLanguage, 'settingsWeightLabel', 'وزن فعلی (کیلوگرم)')}</label>
                <input
                  type="number"
                  value={appState.settings.currentWeight || ''}
                  onChange={(e) => handleProfileChange('currentWeight', Number(e.target.value))}
                  placeholder="80"
                  className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 font-mono text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1">{translate(currentLanguage, 'settingsWeightGoalLabel', 'وزن هدف (کیلوگرم)')}</label>
                <input
                  type="number"
                  value={appState.settings.weightGoal || ''}
                  onChange={(e) => handleProfileChange('weightGoal', Number(e.target.value))}
                  placeholder="72"
                  className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 font-mono text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1">{translate(currentLanguage, 'settingsBodyFatGoalLabel', 'چربی هدف (٪)')}</label>
                <input
                  type="number"
                  value={appState.settings.bodyFatGoal || ''}
                  onChange={(e) => handleProfileChange('bodyFatGoal', Number(e.target.value))}
                  placeholder="18"
                  className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 font-mono text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1">{translate(currentLanguage, 'settingsAgeLabel', 'سن (سال)')}</label>
                <input
                  type="number"
                  value={appState.settings.age || ''}
                  onChange={(e) => handleProfileChange('age', Number(e.target.value))}
                  placeholder="30"
                  className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 font-mono text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="space-y-2 text-xs">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1">{translate(currentLanguage, 'settingsGenderLabel', 'جنسیت')}</label>
                <select
                  value={appState.settings.gender || 'male'}
                  onChange={(e) => handleProfileChange('gender', e.target.value as 'male' | 'female')}
                  className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white"
                >
                  <option value="male">{translate(currentLanguage, 'genderMale', 'آقا (مرد)')}</option>
                  <option value="female">{translate(currentLanguage, 'genderFemale', 'خانم (زن)')}</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1">{translate(currentLanguage, 'settingsActivityLevelLabel', 'سطح فعالیت روزانه')}</label>
                <select
                  value={appState.settings.activityLevel || 'moderate'}
                  onChange={(e) => handleProfileChange('activityLevel', e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white"
                >
                  <option value="sedentary">{translate(currentLanguage, 'actSedentary', 'کم‌تحرک (کار پشت‌میزی)')}</option>
                  <option value="light">{translate(currentLanguage, 'actLight', 'فعالیت سبک (۱-۳ روز ورزش/پیاده‌روی)')}</option>
                  <option value="moderate">{translate(currentLanguage, 'actModerate', 'فعالیت متوسط (۳-۵ روز ورزش در هفته)')}</option>
                  <option value="active">{translate(currentLanguage, 'actActive', 'فعالیت زیاد (۶-۷ روز ورزش سنگین)')}</option>
                  <option value="very_active">{translate(currentLanguage, 'actVeryActive', 'خیلی فعال (ورزشکار حرفه‌ای/کار بدنی)')}</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1">{translate(currentLanguage, 'settingsGoalTypeLabel', 'هدف وزنی شما')}</label>
                <select
                  value={appState.settings.weightGoalType || 'lose'}
                  onChange={(e) => handleProfileChange('weightGoalType', e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white"
                >
                  <option value="lose">{translate(currentLanguage, 'goalTypeLose', 'کاهش وزن (کاهش چربی سالم ~ ۵۰۰ کالری نقصان)')}</option>
                  <option value="maintain">{translate(currentLanguage, 'goalTypeMaintain', 'تثبیت و حفظ وزن فعلی')}</option>
                  <option value="gain">{translate(currentLanguage, 'goalTypeGain', 'افزایش وزن و عضله‌سازی (عالی برای حجم)')}</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] font-bold text-gray-500">{translate(currentLanguage, 'settingsMedicalCondLabel', 'بیماری‌ها و سوابق پزشکی')}</label>
                  <VoiceInputButton
                    currentValue={appState.settings.medicalConditions || ''}
                    onUpdate={(val) => handleProfileChange('medicalConditions', val)}
                    title={translate(currentLanguage, 'voiceInputNormal', 'ورودی صوتی')}
                  />
                </div>
                <input
                  type="text"
                  value={appState.settings.medicalConditions || ''}
                  onChange={(e) => handleProfileChange('medicalConditions', e.target.value)}
                  placeholder={translate(currentLanguage, 'settingsMedicalCondPh', 'مثلاً: کبد چرب، فشار خون')}
                  className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 text-xs text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] font-bold text-gray-500">{translate(currentLanguage, 'settingsMedsLabel', 'داروهای مصرفی و مکمل‌ها')}</label>
                  <VoiceInputButton
                    currentValue={appState.settings.medications || ''}
                    onUpdate={(val) => handleProfileChange('medications', val)}
                    title={translate(currentLanguage, 'voiceInputNormal', 'ورودی صوتی')}
                  />
                </div>
                <input
                  type="text"
                  value={appState.settings.medications || ''}
                  onChange={(e) => handleProfileChange('medications', e.target.value)}
                  placeholder={translate(currentLanguage, 'settingsMedsPh', 'مثلاً: ویتامین D3، متفورمین')}
                  className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 text-xs text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] font-bold text-gray-500">{translate(currentLanguage, 'settingsAllergiesLabel', 'حساسیت‌ها و آلرژی‌ها')}</label>
                  <VoiceInputButton
                    currentValue={appState.settings.allergies || ''}
                    onUpdate={(val) => handleProfileChange('allergies', val)}
                    title={translate(currentLanguage, 'voiceInputNormal', 'ورودی صوتی')}
                  />
                </div>
                <input
                  type="text"
                  value={appState.settings.allergies || ''}
                  onChange={(e) => handleProfileChange('allergies', e.target.value)}
                  placeholder={translate(currentLanguage, 'settingsAllergiesPh', 'مثلاً: حساسیت به پنسلین، لاکتوز')}
                  className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 text-xs text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] font-bold text-gray-500">{translate(currentLanguage, 'settingsDietGoalsLabel', 'اهداف سلامتی و تغذیه‌ای')}</label>
                  <VoiceInputButton
                    currentValue={appState.settings.dietaryGoals || ''}
                    onUpdate={(val) => handleProfileChange('dietaryGoals', val)}
                    title={translate(currentLanguage, 'voiceInputNormal', 'ورودی صوتی')}
                  />
                </div>
                <input
                  type="text"
                  value={appState.settings.dietaryGoals || ''}
                  onChange={(e) => handleProfileChange('dietaryGoals', e.target.value)}
                  placeholder={translate(currentLanguage, 'settingsDietGoalsPh', 'مثلاً: کاهش چربی شکمی، تنظیم قند خون')}
                  className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 text-xs text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <button
              type="button"
              disabled={calculatingAiCalories}
              onClick={async () => {
                const h = appState.settings.height || 175;
                const w = appState.settings.currentWeight || 80;
                const weightGoal = appState.settings.weightGoal || 75;
                const age = appState.settings.age || 30;
                const gender = appState.settings.gender || 'male';
                const act = appState.settings.activityLevel || 'moderate';
                const goal = appState.settings.weightGoalType || 'lose';

                setCalculatingAiCalories(true);
                setAiCalorieData(null);

                const prompt = `لطفاً بر اساس مشخصات بیومتریک و آناتومی زیر، میزان BMR، کالری TDEE و هدف دقیق کالری روزانه (Target Calories) را برای فرد تحلیل و محاسبه کنید:
- قد: ${h} سانتی‌متر
- وزن فعلی: ${w} کیلوگرم
- وزن هدف: ${weightGoal} کیلوگرم
- سن: ${age} سال
- جنسیت: ${gender === 'male' ? 'مرد (آقا)' : 'زن (خانم)'}
- سطح فعالیت: ${act}
- هدف وزنی: ${goal === 'lose' ? 'کاهش وزن' : goal === 'gain' ? 'افزایش وزن و عضله‌سازی' : 'تثبیت وزن'}

لطفاً تحلیل را کاملاً فارسی و روان ارائه دهید. به هیچ وجه از علائم لاتک مانند \( \) یا \text در متن استفاده نکنید و محاسبات را به فرم ساده ریاضی بنویسید. حتماً عدد کالری هدف روزانه را هم به وضوح ذکر کنید (مثلاً: target_calories: 2150).`;

                try {
                  const aiReply = await sendWorkerAiRequest('nutrition', prompt, []);
                  const parsed = parseNutritionReply(aiReply, prompt);
                  let extractedCal = parsed.calories;

                  if (!extractedCal || extractedCal < 800 || extractedCal > 6000) {
                    let bmr = (10 * w) + (6.25 * h) - (5 * age) + (gender === 'male' ? 5 : -161);
                    const mults: Record<string, number> = { sedentary: 1.2, light: 1.375, moderate: 1.55, active: 1.725, very_active: 1.9 };
                    let calc = bmr * (mults[act] || 1.55);
                    if (goal === 'lose') calc -= 500;
                    else if (goal === 'gain') calc += 400;
                    extractedCal = Math.max(1200, Math.round(calc));
                  }

                  const updatedState = {
                    ...appState,
                    settings: {
                      ...appState.settings,
                      targetCalories: extractedCal
                    }
                  };
                  setAppState(updatedState);
                  saveAppState(updatedState);
                  onSettingsUpdated();

                  setAiCalorieData({ targetCalories: extractedCal, rawReply: aiReply });
                } catch (err: any) {
                  console.error('AI Calorie Calculation error:', err);
                  let bmr = (10 * w) + (6.25 * h) - (5 * age) + (gender === 'male' ? 5 : -161);
                  const mults: Record<string, number> = { sedentary: 1.2, light: 1.375, moderate: 1.55, active: 1.725, very_active: 1.9 };
                  let calc = bmr * (mults[act] || 1.55);
                  if (goal === 'lose') calc -= 500;
                  else if (goal === 'gain') calc += 400;
                  const finalTarget = Math.max(1200, Math.round(calc));

                  const updatedState = {
                    ...appState,
                    settings: {
                      ...appState.settings,
                      targetCalories: finalTarget
                    }
                  };
                  setAppState(updatedState);
                  saveAppState(updatedState);
                  onSettingsUpdated();

                  setAiCalorieData({
                    targetCalories: finalTarget,
                    rawReply: `بر اساس فرمول آناتومیک Mifflin-St Jeor، هدف کالری روزانه شما محاسبه گردید.\n\n- میزان BMR پایه: ${Math.round(bmr)} کیلوکالری\n- میزان TDEE روزانه: ${Math.round(bmr * (mults[act] || 1.55))} کیلوکالری\n- هدف پیشنهادی برای ${goal === 'lose' ? 'کاهش وزن' : goal === 'gain' ? 'افزایش وزن' : 'تثبیت وزن'}: ${finalTarget} کیلوکالری`
                  });
                } finally {
                  setCalculatingAiCalories(false);
                }
              }}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-2 shadow-md shadow-indigo-500/20 cursor-pointer transition-all disabled:opacity-50"
            >
              {calculatingAiCalories ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin text-white" />
                  <span>{translate(currentLanguage, 'settingsAiCalibrating', 'در حال آنالیز و محاسبه با هوش مصنوعی...')}</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 text-emerald-300 animate-pulse" />
                  <span>{translate(currentLanguage, 'settingsAiCalibrateBtn', 'محاسبه و تنظیم هوشمند هدف کالری با هوش مصنوعی')}</span>
                </>
              )}
            </button>

            {aiCalorieData && (
              <FormattedAiCalorieAnalysis 
                targetCalories={aiCalorieData.targetCalories} 
                rawReply={aiCalorieData.rawReply} 
              />
            )}
          </div>

          {/* TARGET WEIGHT FORECAST & TIMELINE CARD */}
          {(() => {
            const w = appState.settings.currentWeight;
            const g = appState.settings.weightGoal;
            const h = appState.settings.height || 175;
            const a = appState.settings.age || 30;
            const gender = appState.settings.gender || 'male';
            const act = appState.settings.activityLevel || 'moderate';
            const targetCal = appState.settings.targetCalories || 2200;

            if (!w || !g) return null;

            const bmr = (10 * w) + (6.25 * h) - (5 * a) + (gender === 'male' ? 5 : -161);
            const mults: Record<string, number> = { sedentary: 1.2, light: 1.375, moderate: 1.55, active: 1.725, very_active: 1.9 };
            const tdee = Math.round(bmr * (mults[act] || 1.55));
            const weightDiff = Math.abs(w - g);

            let weeks: number | null = null;
            let months: number | null = null;
            let kgPerWeek = 0;
            let delta = 0;
            let isLoss = w > g;
            let isGain = w < g;
            let isSame = w === g;
            let targetDateStr = '';

            if (isLoss) {
              delta = tdee - targetCal; // positive = deficit
              if (delta > 0) {
                kgPerWeek = Number(((delta * 7) / 7700).toFixed(2));
                weeks = Math.max(1, Math.round(weightDiff / kgPerWeek));
                months = Number((weeks / 4.33).toFixed(1));
                const targetDate = new Date();
                targetDate.setDate(targetDate.getDate() + weeks * 7);
                targetDateStr = formatGregorianDate(targetDate, currentLanguage, 'medium');
              }
            } else if (isGain) {
              delta = targetCal - tdee; // positive = surplus
              if (delta > 0) {
                kgPerWeek = Number(((delta * 7) / 7700).toFixed(2));
                weeks = Math.max(1, Math.round(weightDiff / kgPerWeek));
                months = Number((weeks / 4.33).toFixed(1));
                const targetDate = new Date();
                targetDate.setDate(targetDate.getDate() + weeks * 7);
                targetDateStr = formatGregorianDate(targetDate, currentLanguage, 'medium');
              }
            }

            return (
              <div className="premium-card p-5 dark:bg-gray-800 space-y-4 border border-emerald-500/30 bg-emerald-500/5 shadow-md">
                <div className="flex items-center justify-between border-b border-emerald-100 dark:border-emerald-900/40 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 rounded-xl">
                      <Target className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-gray-900 dark:text-white font-display">
                        {translate(currentLanguage, 'targetWeightEtaTitle', 'پیش‌بینی زمان رسیدن به وزن هدف')}
                      </h3>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">
                        {translate(currentLanguage, 'targetWeightEtaDesc', 'محاسبه سرعت کاهش یا افزایش وزن با توجه به کالری هدف روزانه')}
                      </p>
                    </div>
                  </div>
                  <div className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-lg bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">
                    TDEE: {formatNumberByLang(tdee, currentLanguage)} kcal
                  </div>
                </div>

                {isSame ? (
                  <div className="p-3 rounded-xl bg-emerald-100/60 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 text-xs font-bold flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>{translate(currentLanguage, 'targetWeightReached', 'تبریک! شما در حال حاضر در وزن هدف خود هستید.')}</span>
                  </div>
                ) : weeks ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-100 dark:border-gray-800 space-y-1">
                        <span className="text-[10px] text-gray-400 block font-semibold">
                          {translate(currentLanguage, 'targetWeightEstimatedTime', 'زمان تخمینی تا وزن هدف:')}
                        </span>
                        <div className="text-sm font-black text-emerald-600 dark:text-emerald-400">
                          {translate(currentLanguage, 'targetWeightWeeks', 'حدود {{weeks}} هفته ({{months}} ماه)', {
                            weeks: formatNumberByLang(weeks, currentLanguage),
                            months: formatNumberByLang(months || 0, currentLanguage)
                          })}
                        </div>
                        {targetDateStr && (
                          <span className="text-[10px] text-gray-500 font-mono block">
                            🗓️ ~ {targetDateStr}
                          </span>
                        )}
                      </div>

                      <div className="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-100 dark:border-gray-800 space-y-1">
                        <span className="text-[10px] text-gray-400 block font-semibold">
                          {translate(currentLanguage, 'targetWeightWeeklyTakt', 'تغییر وزن هفتگی:')}
                        </span>
                        <div className="text-sm font-black text-indigo-600 dark:text-indigo-400 font-mono">
                          {isLoss ? '📉 -' : '📈 +'}{formatNumberByLang(kgPerWeek, currentLanguage)} kg / {currentLanguage === 'sv' ? 'vecka' : currentLanguage === 'en' ? 'week' : 'هفته'}
                        </div>
                        <span className="text-[10px] text-gray-500 block">
                          {isLoss ? (currentLanguage === 'sv' ? 'Måttlig och hållbar viktminskning' : currentLanguage === 'en' ? 'Sustainable fat loss' : 'کاهش تدریجی و پایدار') : (currentLanguage === 'sv' ? 'Kontrollerad muskelökning' : currentLanguage === 'en' ? 'Controlled lean gain' : 'افزایش حجم کنترل‌شده')}
                        </span>
                      </div>

                      <div className="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-100 dark:border-gray-800 space-y-1">
                        <span className="text-[10px] text-gray-400 block font-semibold">
                          {isLoss ? (currentLanguage === 'sv' ? 'Dagligt kaloriunderskott:' : currentLanguage === 'en' ? 'Daily calorie deficit:' : 'کسری کالری روزانه:') : (currentLanguage === 'sv' ? 'Dagligt kaloriöverskott:' : currentLanguage === 'en' ? 'Daily calorie surplus:' : 'مازاد کالری روزانه:')}
                        </span>
                        <div className="text-sm font-black text-amber-600 dark:text-amber-400 font-mono">
                          {isLoss ? '-' : '+'}{formatNumberByLang(delta, currentLanguage)} kcal / {currentLanguage === 'sv' ? 'dag' : currentLanguage === 'en' ? 'day' : 'روز'}
                        </div>
                        <span className="text-[10px] text-gray-500 font-mono block">
                          {formatNumberByLang(targetCal, currentLanguage)} vs {formatNumberByLang(tdee, currentLanguage)} TDEE
                        </span>
                      </div>
                    </div>

                    <div className="p-3 bg-emerald-500/10 rounded-xl text-[11px] text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>
                        {currentLanguage === 'sv' ? (
                          <>Om du i genomsnitt följer <strong>{formatNumberByLang(targetCal, currentLanguage)} kcal/dag</strong> når du din målvikt på <strong>{formatNumberByLang(g, currentLanguage)} kg</strong> från <strong>{formatNumberByLang(w, currentLanguage)} kg</strong> efter cirka <strong>{formatNumberByLang(weeks, currentLanguage)} veckor</strong>.</>
                        ) : currentLanguage === 'en' ? (
                          <>If you average <strong>{formatNumberByLang(targetCal, currentLanguage)} kcal/day</strong>, you will reach your target weight of <strong>{formatNumberByLang(g, currentLanguage)} kg</strong> from <strong>{formatNumberByLang(w, currentLanguage)} kg</strong> in about <strong>{formatNumberByLang(weeks, currentLanguage)} weeks</strong>.</>
                        ) : (
                          <>اگر هر روز میانگین <strong>{formatNumberByLang(targetCal, currentLanguage)} کیلوکالری</strong> مصرف نمایید، پس از <strong>{formatNumberByLang(weeks, currentLanguage)} هفته</strong> از وزن <strong>{formatNumberByLang(w, currentLanguage)} kg</strong> به وزن هدف <strong>{formatNumberByLang(g, currentLanguage)} kg</strong> خواهید رسید.</>
                        )}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 rounded-xl bg-amber-100/60 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 text-xs font-semibold">
                    {translate(currentLanguage, 'targetWeightMaintain', 'کالری در سطح تثبیت وزن قرار دارد (تغییر وزن صفر)')}
                  </div>
                )}
              </div>
            );
          })()}

          {/* AI Service Status */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              {translate(currentLanguage, 'settingsAiStatusTitle', 'وضعیت سرویس هوش مصنوعی آنلاین')}
            </h3>
            <p className="text-[11px] text-gray-400 leading-relaxed">
              {translate(currentLanguage, 'settingsAiStatusDesc', 'برنامه برای تحلیل تغذیه، تخمین کالری وعده‌ها و تولید برنامه‌های توانبخشی از سرویس هوش مصنوعی آنلاین استفاده می‌کند.')}
            </p>

            <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500 shrink-0" />
              <span>{translate(currentLanguage, 'settingsAiStatusConnected', 'ارتباط با سرویس هوش مصنوعی فعال و برقرار است.')}</span>
            </div>
          </div>

        </div>

      </div>

    </div>
  );
}
